/**
 * CsatSurvey — Phase 5.7 public survey page.
 *
 * Route: /csat/:token (no auth). Renders the customer-facing rating form for
 * a pending magic-link token, OR a graceful 'thank you' / 'expired' / 'invalid
 * link' state. Submits POST /api/csat/:token with `{score, comment}`.
 *
 * No `api` axios instance — that one injects auth headers. We use plain fetch
 * with the same REACT_APP_BACKEND_URL prefix so the page works without a session.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, AlertTriangle, Star, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const SCORE_LABEL = {
  1: "Very unsatisfied", 2: "Unsatisfied", 3: "Neutral",
  4: "Satisfied", 5: "Very satisfied",
};

export default function CsatSurvey() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initialScore = Number(params.get("score")) || 0;

  const [state, setState] = useState({ loading: true });
  const [score, setScore] = useState(initialScore);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { score, ticket_code }

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/csat/${token}`);
        const d = await r.json();
        setState({ loading: false, ...d });
      } catch (e) {
        setState({ loading: false, status: "not_found" });
      }
    };
    load();
  }, [token]);

  const onSubmit = async () => {
    if (score < 1 || score > 5) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/csat/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ score, comment }),
      });
      if (r.ok) {
        const d = await r.json();
        setSubmitted({ score: d.score, ticket_code: d.ticket_code });
      } else {
        const d = await r.json().catch(() => ({}));
        if (r.status === 410) {
          // Already submitted in another tab — treat as success
          setSubmitted({ score: d.score || score, ticket_code: state.ticket_code });
        } else {
          alert(d.detail || "Could not record your response. Please refresh and try again.");
        }
      }
    } catch (e) {
      alert("Network error — please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50/40 to-white px-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200" data-testid="csat-survey">
        <CardContent className="p-8">{children}</CardContent>
      </Card>
    </div>
  );

  if (state.loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-8 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading survey…</span>
        </div>
      </Shell>
    );
  }

  if (submitted || state.status === "submitted") {
    const finalScore = submitted?.score || state.score;
    return (
      <Shell>
        <div className="text-center py-4" data-testid="csat-thanks">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <h1 className="text-xl font-heading font-semibold text-slate-800 mb-1">Thank you!</h1>
          <p className="text-sm text-slate-500">Your feedback for ticket <span className="font-mono text-slate-700">{submitted?.ticket_code || state.ticket_code}</span> has been recorded.</p>
          <div className="mt-5 inline-flex items-center gap-1 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} className={`w-4 h-4 ${n <= (finalScore || 0) ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
            ))}
            <span className="text-xs text-slate-600 ml-2">{SCORE_LABEL[finalScore] || ""}</span>
          </div>
          <p className="mt-6 text-[11px] text-slate-400">Kippu by Zefficacy</p>
        </div>
      </Shell>
    );
  }

  if (state.status === "expired") {
    return (
      <Shell>
        <div className="text-center py-4" data-testid="csat-expired">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-heading font-semibold text-slate-800 mb-1">Survey link expired</h1>
          <p className="text-sm text-slate-500">This feedback link is no longer active. If you'd still like to share thoughts, please reply to your ticket email.</p>
        </div>
      </Shell>
    );
  }

  if (state.status === "not_found" || !state.status) {
    return (
      <Shell>
        <div className="text-center py-4" data-testid="csat-not-found">
          <AlertTriangle className="w-12 h-12 mx-auto text-rose-500 mb-3" />
          <h1 className="text-xl font-heading font-semibold text-slate-800 mb-1">Survey not found</h1>
          <p className="text-sm text-slate-500">We couldn't locate this survey. The link may be malformed or revoked.</p>
        </div>
      </Shell>
    );
  }

  // Pending — render the rater
  return (
    <Shell>
      <div className="text-center mb-5" data-testid="csat-form">
        <div className="text-[11px] uppercase tracking-[2px] text-indigo-500 font-semibold">Customer feedback</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-800 mt-1">How did we do?</h1>
        <p className="text-sm text-slate-500 mt-2">
          Hi {state.customer_name || "there"}, your ticket <span className="font-mono text-slate-700">{state.ticket_code}</span> was just resolved.
          Tap a star to rate your experience.
        </p>
      </div>

      <div className="flex justify-center gap-1.5 mb-1">
        {[1,2,3,4,5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setScore(n)}
            onMouseEnter={() => {}}
            aria-label={`${n} star${n>1?"s":""}`}
            data-testid={`csat-star-${n}`}
            className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-indigo-300 rounded">
            <Star className={`w-9 h-9 ${n <= score ? "fill-amber-500 text-amber-500" : "text-slate-300 hover:text-amber-400"}`} />
          </button>
        ))}
      </div>
      <div className="text-center h-5 text-xs text-slate-500 mb-4">
        {score > 0 ? SCORE_LABEL[score] : <span className="text-slate-300">Pick a rating</span>}
      </div>

      <div className="space-y-1.5">
        <label className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">
          Anything you'd like to share? <span className="text-slate-400 normal-case font-normal">(optional)</span>
        </label>
        <Textarea
          rows={3}
          maxLength={1000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={score >= 4 ? "What worked well?" : "What could we have done better?"}
          className="resize-none text-sm"
          data-testid="csat-comment" />
      </div>

      <Button
        onClick={onSubmit}
        disabled={score < 1 || submitting}
        className="w-full mt-5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400"
        data-testid="csat-submit">
        {submitting ? "Sending…" : "Submit feedback"}
      </Button>

      <p className="mt-5 text-center text-[10px] text-slate-400">
        Kippu by Zefficacy · This survey link is valid for 7 days.
      </p>
    </Shell>
  );
}
